import { windowWrapper } from '@clubmed/utils';
import { get } from 'lodash';
import moment from 'moment';
import { batch } from 'react-redux';
import type {
  FetchCalendarOptions,
  FetchBookableDatesOptions,
} from 'Store/calendar/calendar.helper';
import { queryFetchCalendar, queryFetchBookableDates } from 'Store/calendar/calendar.helper';
import {
  CALENDAR_CHANGE_MONTH,
  CALENDAR_SET_BLOCK_INPUT_DEPARTURE_DATE,
  CALENDAR_SET_INITIAL_DURATION,
  CALENDAR_SET_IS_PRICE_CALENDAR_SELCTION,
  CLEAR_CALENDAR,
  FETCH_BOOKABLE_DATES,
  FETCH_BOOKABLE_DATES_ABORT,
  FETCH_CALENDAR,
  FETCH_CALENDAR_ABORT,
  FETCH_CALENDAR_TRIP_PRICE,
  FETCH_CALENDAR_TRIP_PRICE_ABORT,
} from 'Store/constants';
import { fetchPrice } from 'Store/priceTicket/priceTicket.helper';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { getDepartureDate } from 'Store/trip-date-criteria/storeHelper';
import { updateTripDateCriteria } from 'Store/trip-date-criteria/tripDateCriteria.action';
import createAction, { createOptimisticAction } from 'Store/utils/createAction';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { formatTimestampDate } from 'Utils/date/dateUtils';
import { QUERY_DEPARTURE_DATE } from 'Utils/uri/constants';
import { getQueryParameters } from 'Utils/uri/uriUtils';
export function fetchCalendar(options: FetchCalendarOptions, appContext: AppContext) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const calendarRequestInProgress =
      state.calendar?.isLoading && state.calendar?.lastRequestId !== -1;

    if (
      calendarRequestInProgress ||
      JSON.stringify(options) === JSON.stringify(state.calendar?.calendarCriteria ?? {})
    ) {
      return dispatch(createAction(FETCH_CALENDAR_ABORT, {}));
    }

    return dispatch(
      createOptimisticAction(
        FETCH_CALENDAR,
        {
          departureCity: options.transport,
          productId: options.resortId,
          options,
        },
        {
          optimist: true,
          promise: queryFetchCalendar(appContext, options),
        },
      ),
    );
  };
}
export function fetchCalendarTripPrice(options: {
  appContext: AppContext;
  duration?: number;
  forceAdultPerTrip?: boolean;
  location: any;
  productId: string;
  transport?: string;
  withDate?: boolean;
}) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const calendarRequestInProgress =
      state.calendar?.isLoading && state.calendar?.lastRequestId !== -1;

    if (calendarRequestInProgress) {
      return dispatch(createAction(FETCH_CALENDAR_TRIP_PRICE_ABORT, {}));
    }

    return dispatch(
      createOptimisticAction(
        FETCH_CALENDAR_TRIP_PRICE,
        {},
        {
          optimist: true,
          promise: fetchPrice(options),
        },
      ),
    );
  };
}
export function fetchBookableDates(options: FetchBookableDatesOptions, appContext: AppContext) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const state = getState();
    const bookableDateRequestInProgress =
      state.calendar?.bookableDatesLoading && state.calendar?.bookableDateLastRequestId !== -1;

    if (
      bookableDateRequestInProgress ||
      (options.departureCity === state.calendar?.bookableDatesCriteria.departureCity &&
        options.productId === state.calendar?.bookableDatesCriteria.productId)
    ) {
      return dispatch(createAction(FETCH_BOOKABLE_DATES_ABORT, {}));
    }

    return dispatch(
      createOptimisticAction(
        FETCH_BOOKABLE_DATES,
        {},
        {
          optimist: true,
          promise: queryFetchBookableDates(appContext, options),
        },
      ),
    );
  };
}
export function selectMonth(selectedMonth: number) {
  // format YYYYMM (number)
  return createAction(CALENDAR_CHANGE_MONTH, {
    selectedMonth,
  });
}

function determineDepartureDate(state) {
  if (!getDepartureDate(state)) {
    const departureDateFromUrl = get(
      getQueryParameters(windowWrapper.getLocation()),
      QUERY_DEPARTURE_DATE,
    );
    return formatTimestampDate(departureDateFromUrl, API_DATE_FORMAT) || getDepartureDate(state);
  }

  return formatTimestampDate(getDepartureDate(state), API_DATE_FORMAT);
}

export function setPossibleDepartureDate() {
  return (dispatch: ReduxDispatch, getState: () => ReduxState) => {
    const findDayInMonths = (months = [], dayId = '') => {
      const id = parseInt(dayId, 10);
      const allDays = months.reduce((acc, m) => [].concat(acc, m.days), []);
      return allDays.find((d) => get(d, 'dayContent.id') === id);
    };

    const departureDate = determineDepartureDate(getState());

    if (departureDate) {
      const calendarMonth = get(getState(), 'calendar.calendar.months', []) as Array<{
        days: Array<{ dayContent?: { id?: string | number; canGoToBooking?: boolean } }>;
      }>;
      const day = findDayInMonths(calendarMonth, String(departureDate));
      const dayContent = get(day, 'dayContent', null);

      if (dayContent) {
        if (get(dayContent, 'canGoToBooking')) {
          const yearAndMonth = Math.floor(Number(dayContent.id) / 100);
          batch(() => {
            dispatch(
              updateTripDateCriteria({
                day: dayContent,
              }),
            );
            // makes sure tripCriteria.departureDate in store is synchronised
            // TODO: merge tripDateCriteria into tripCriteria #12563
            dispatch(setDepartureDate(moment(`${dayContent.id}`, API_DATE_FORMAT).valueOf()));
            dispatch(selectMonth(yearAndMonth));
          });
        } else {
          batch(() => {
            dispatch(
              updateTripDateCriteria({
                day: null,
              }),
            );
            dispatch(setDepartureDate(null));
          });
        }
      }
    }
  };
}
export const setInitialDuration = (duration: number | null | undefined) => ({
  type: CALENDAR_SET_INITIAL_DURATION,
  payload: {
    duration,
  },
});
export const setIsPriceCalendarSelection = (isPriceCalendarSelection: boolean) => ({
  type: CALENDAR_SET_IS_PRICE_CALENDAR_SELCTION,
  payload: {
    isPriceCalendarSelection,
  },
});
export const setCalendarBlockInputDepartureDate = (blockInputDepartureDate: boolean) => ({
  type: CALENDAR_SET_BLOCK_INPUT_DEPARTURE_DATE,
  payload: {
    blockInputDepartureDate,
  },
});
export const clearCalendar = () => ({
  type: CLEAR_CALENDAR,
});
